import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiCheckCircle, FiShield, FiFileText, FiHome, 
  FiMapPin, FiCamera, FiClock, FiAlertTriangle, FiTool, 
  FiLayers, FiCopy, FiCheck, FiX, FiHelpCircle, FiInfo 
} = FiIcons;

const CopyableTemplate = () => {
  const [copied, setCopied] = useState(false);
  const templateText = `Ort: Erkelenz
Gebäude: Einfamilienhaus, Altbau ca. 1970
Leistung: Trockenbauwand ca. 4,20 m × 2,60 m, einseitig beplankt
Besonderes: Schallschutz erwünscht
Zustand: Raum leer, Boden gefliest
Zeitraum: innerhalb der nächsten 6–8 Wochen
Fotos: siehe Anhang`;

  const handleCopy = () => {
    navigator.clipboard.writeText(templateText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gray-900 rounded-xl p-6 shadow-xl my-8 relative overflow-hidden">
      <div className="absolute top-0 right-0 p-4">
        <button 
          onClick={handleCopy}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-bold text-sm transition-all ${copied ? 'bg-green-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}
        >
          <SafeIcon icon={copied ? FiCheck : FiCopy} />
          <span>{copied ? 'Kopiert!' : 'Vorlage kopieren'}</span>
        </button>
      </div>
      <h3 className="text-white font-bold mb-4 flex items-center">
        <SafeIcon icon={FiFileText} className="mr-2 text-mk-teal-400"/>
        Muster-Anfrage (Kopierbar)
      </h3>
      <pre className="font-mono text-gray-300 whitespace-pre-wrap bg-black/30 p-4 rounded-lg text-sm border border-gray-700">
        {templateText}
      </pre>
      <p className="text-gray-500 text-xs mt-3 italic">
        👉 Klicken Sie auf "Kopieren" und fügen Sie den Text in Ihre E-Mail ein.
      </p>
    </div>
  );
};

function TrockenbauAnfrage() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Trockenbau-Anfrage richtig stellen: So bekommt jedes Trockenbau-Unternehmen alle Infos – ohne Rückfragen",
    "description": "Praxis-Checkliste für eine vollständige Trockenbau-Anfrage: Ort, Maße, Anforderungen, Fotos und Zeitraum. So sparen Kunden und Trockenbauer Zeit und erhalten schneller ein belastbares Angebot – auch in NRW.",
    "inLanguage": "de-DE",
    "author": {
      "@type": "Organization",
      "name": "MK-Ausbau",
      "url": "https://ausbau.nrw"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MK-Ausbau",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ausbau.nrw/logo-icon.svg"
      }
    },
    "datePublished": "2026-05-02",
    "image": "https://mx.ausbau.nrw/img/Trocken_und_Akustikbau_xl.jpg",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://ausbau.nrw/blog/trockenbau-anfrage-richtig-stellen/"
    },
    "keywords": [
      "trockenbau anfrage",
      "trockenbau angebot",
      "trockenbau nrw",
      "innenausbau nrw",
      "trockenbau checkliste",
      "trockenbauer angebot anfrage"
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Welche Infos braucht ein Trockenbauer für ein Angebot ohne Rückfragen?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ort, Gebäudeart (Altbau/Neubau), genaue Leistung (Wand/Decke/Dachschräge), Maße oder grobe Flächen, Anforderungen (Schallschutz/Brandschutz/Feuchtraum), aktueller Zustand (leer/möbliert), Fotos und gewünschter Zeitraum."
        }
      },
      {
        "@type": "Question",
        "name": "Reichen Handyfotos wirklich aus?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja. 2–5 Fotos (Gesamtansicht + Details) sparen oft mehrere Rückfragen und helfen, ein realistisches Angebot zu erstellen."
        }
      },
      {
        "@type": "Question",
        "name": "Muss ich exakte Maße liefern?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Nicht zwingend. Grobe Maße oder geschätzte Flächen reichen häufig für eine erste Kalkulation. Exakte Maße werden später beim Aufmaß oder vor Ort geprüft."
        }
      },
      {
        "@type": "Question",
        "name": "Was sollte ich bei Schallschutz oder Brandschutz angeben?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ob es eine Mietwohnung, ein Schlafzimmer, ein Kinderzimmer oder eine Trennwand zwischen Nutzungseinheiten ist. Bei Brandschutz: ob es gewerblich ist oder besondere Anforderungen bestehen. Der Aufbau wird dann passend geplant."
        }
      },
      {
        "@type": "Question",
        "name": "Warum lehnen manche Firmen Anfragen ab?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Häufig, weil Informationen fehlen und der Aufwand für Rückfragen zu hoch ist. Vollständige Anfragen erhöhen die Chance auf schnelle Rückmeldung deutlich."
        }
      },
      {
        "@type": "Question",
        "name": "Gilt das auch für Trockenbau in NRW?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja. Gerade in NRW (z. B. Heinsberg bis Düsseldorf) sind Gebäudetypen und Bestände sehr unterschiedlich. Gute Infos machen Angebote schneller und präziser."
        }
      },
      {
        "@type": "Question",
        "name": "Kann ich mehrere Leistungen in einer Anfrage bündeln?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja. Trockenbau + Dämmung + Maler + Boden kann sinnvoll sein, weil die Baustelle besser koordiniert wird und Zeit spart."
        }
      }
    ]
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Trockenbau Anfrage richtig stellen | Checkliste & Muster" 
        description="Praxis-Checkliste für eine vollständige Trockenbau-Anfrage: Ort, Maße, Anforderungen, Fotos und Zeitraum. So sparen Kunden und Trockenbauer Zeit." 
        keywords="Trockenbau Anfrage stellen, Handwerker Angebot anfordern, Trockenbau NRW, Checkliste Handwerkeranfrage, Angebot Mustertext"
        schema={[articleSchema, faqSchema]}
        canonical="/blog/trockenbau-anfrage-richtig-stellen"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <Link to="/blog" className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group" >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article 
          {...fadeIn}
          className="prose prose-lg prose-mk max-w-none"
        >
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4 text-left">
              <SafeIcon icon={FiFileText} />
              <span>Ratgeber & Checklisten</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-6 text-left">
              Trockenbau-Anfrage richtig stellen: So bekommt jedes Trockenbau-Unternehmen alle Infos – ohne Rückfragen
            </h1>
            <p className="text-xl font-medium text-mk-teal-700 mb-6">
              Ohne Rückfragen, ohne Zeitverlust – der Leitfaden für Bauherren
            </p>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Praxis-Guide</span>
              <span className="flex items-center"><SafeIcon icon={FiInfo} className="mr-1" /> Für Bauherren & Eigentümer</span>
            </div>
          </header>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12 text-left">
            Viele Bauherren, Vermieter und Eigentümer wundern sich, warum sie nach einer Anfrage für Trockenbau in NRW mehrere Rückfragen bekommen – oder gar kein Angebot.
            <br/><br/>
            Die Wahrheit ist simpel: <br/>
            👉 <strong>90 % aller Trockenbau-Anfragen sind unvollständig.</strong>
          </div>

          {/* Warum wichtig */}
          <div className="bg-mk-teal-50 border-l-4 border-mk-teal-500 p-6 rounded-r-xl mb-12">
            <h3 className="text-mk-teal-900 font-bold mt-0 mb-4">Das kostet Zeit:</h3>
            <ul className="space-y-2 mb-4 list-none pl-0">
              <li className="flex items-center"><SafeIcon icon={FiX} className="text-mk-red-500 mr-2"/> für den Kunden</li>
              <li className="flex items-center"><SafeIcon icon={FiX} className="text-mk-red-500 mr-2"/> für den Trockenbauer</li>
              <li className="flex items-center"><SafeIcon icon={FiAlertTriangle} className="text-orange-500 mr-2"/> und verzögert das Projekt unnötig</li>
            </ul>
            <p className="text-mk-teal-800 m-0 text-sm">
              Dieser Leitfaden zeigt neutral und praxisnah, wie man bei jedem Trockenbau-Unternehmen eine Anfrage stellt, die so vollständig ist, dass oft kein Rückruf mehr nötig ist.
            </p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Warum vollständige Trockenbau-Anfragen so wichtig sind</h2>
          <p className="text-gray-700 mb-6">
            Trockenbau ist kein Pauschalprodukt. Schon kleine Unterschiede verändern Materialaufbau, Arbeitszeit und Kosten massiv. Fehlen Informationen, muss der Betrieb nachfragen, ungenau schätzen (was seriöse Firmen ungern tun) oder die Anfrage ablehnen.
          </p>
          <p className="text-gray-700">
            Gerade in NRW – mit sehr unterschiedlichen Gebäudetypen von Heinsberg bis Düsseldorf – ist Klarheit entscheidend.
          </p>

          {/* Die 7 Punkte */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-8">Die 7 wichtigsten Angaben für jede Trockenbau-Anfrage</h2>
          
          <div className="space-y-8 mb-12">
            {/* 1. Ort */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-mk-teal-100 rounded-full flex items-center justify-center text-mk-teal-600 font-bold mr-4">1</div>
                <h3 className="text-xl font-bold text-gray-900 m-0 flex items-center">
                  <SafeIcon icon={FiMapPin} className="mr-2"/> Wo findet das Projekt statt?
                </h3>
              </div>
              <p className="text-gray-700 mb-2">📍 Ort + Stadt reichen völlig (z. B. Geilenkirchen, Neuss, Düsseldorf).</p>
              <p className="text-xs text-gray-500 uppercase font-bold">Warum wichtig:</p>
              <p className="text-sm text-gray-600">Anfahrt & Logistik, regionale Auslastung, realistische Terminplanung.</p>
            </div>

            {/* 2. Gebäude */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-mk-teal-100 rounded-full flex items-center justify-center text-mk-teal-600 font-bold mr-4">2</div>
                <h3 className="text-xl font-bold text-gray-900 m-0 flex items-center">
                  <SafeIcon icon={FiHome} className="mr-2"/> Was für ein Gebäude ist es?
                </h3>
              </div>
              <p className="text-gray-700 mb-2">🏠 Bitte klar angeben: Wohnung / Einfamilienhaus / Gewerbe, Altbau oder Neubau.</p>
              <p className="text-xs text-gray-500 uppercase font-bold">Warum wichtig:</p>
              <p className="text-sm text-gray-600">Altbauten brauchen andere Konstruktionen. Unebenheiten & Bestand beeinflussen Aufwand.</p>
            </div>

            {/* 3. Was bauen */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-mk-teal-100 rounded-full flex items-center justify-center text-mk-teal-600 font-bold mr-4">3</div>
                <h3 className="text-xl font-bold text-gray-900 m-0 flex items-center">
                  <SafeIcon icon={FiTool} className="mr-2"/> Was soll gebaut werden?
                </h3>
              </div>
              <p className="text-gray-700 mb-2">So konkret wie möglich: "Trockenbauwand (Länge × Höhe)", "abgehängte Decke (m²)", "Dachschräge verkleiden", "Raum teilen / öffnen".</p>
              <p className="text-sm font-bold text-mk-teal-600 bg-mk-teal-50 inline-block px-2 py-1 rounded">👉 „Ich brauche Trockenbau“ reicht nicht.</p>
            </div>

            {/* 4. Anforderungen */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-mk-teal-100 rounded-full flex items-center justify-center text-mk-teal-600 font-bold mr-4">4</div>
                <h3 className="text-xl font-bold text-gray-900 m-0 flex items-center">
                  <SafeIcon icon={FiShield} className="mr-2"/> Gibt es besondere Anforderungen?
                </h3>
              </div>
              <p className="text-gray-700 mb-2">Bitte ehrlich angeben, wenn relevant:</p>
              <ul className="text-gray-700 list-disc pl-5 mb-2">
                <li>Schallschutz (z. B. Schlafzimmer / Mietwohnung)</li>
                <li>Brandschutz</li>
                <li>Dämmung geplant (ja / nein)</li>
                <li>Feuchtraum (Bad, Keller)</li>
              </ul>
              <p className="text-sm text-gray-600">Fehlt diese Info, ist ein Angebot oft nicht belastbar.</p>
            </div>

            {/* 5. Zustand */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-mk-teal-100 rounded-full flex items-center justify-center text-mk-teal-600 font-bold mr-4">5</div>
                <h3 className="text-xl font-bold text-gray-900 m-0 flex items-center">
                  <SafeIcon icon={FiLayers} className="mr-2"/> Wie ist der aktuelle Zustand?
                </h3>
              </div>
              <p className="text-gray-700 mb-2">Rohbau? Bereits verputzt? Alter Boden / Decke vorhanden? Alles frei zugänglich oder möbliert?</p>
              <p className="text-sm text-gray-600">Das entscheidet über: Schutzmaßnahmen, Baustellenzeit, Zusatzaufwand.</p>
            </div>

            {/* 6. Fotos */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border-l-4 border-l-mk-teal-500">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-mk-teal-100 rounded-full flex items-center justify-center text-mk-teal-600 font-bold mr-4">6</div>
                <h3 className="text-xl font-bold text-gray-900 m-0 flex items-center">
                  <SafeIcon icon={FiCamera} className="mr-2"/> Fotos – der größte Zeitgewinn für beide Seiten 📸
                </h3>
              </div>
              <p className="text-gray-700 mb-2">2–5 Handyfotos sparen oft mehrere Rückfragen: Gesamtansicht, Detail (Decke, Wandanschluss, Schräge), Zugang (Treppenhaus, Raumgröße).</p>
              <p className="text-sm font-bold text-mk-teal-600">👉 Fotos sind wichtiger als perfekte Maße.</p>
            </div>

            {/* 7. Wann */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-mk-teal-100 rounded-full flex items-center justify-center text-mk-teal-600 font-bold mr-4">7</div>
                <h3 className="text-xl font-bold text-gray-900 m-0 flex items-center">
                  <SafeIcon icon={FiClock} className="mr-2"/> Wann soll es ungefähr stattfinden?
                </h3>
              </div>
              <p className="text-gray-700 mb-2">Kein fixes Datum nötig: „in den nächsten 2–3 Monaten“, „nach Fenstertausch“, „sobald Angebot passt“.</p>
              <p className="text-sm text-gray-600">Das hilft bei: realistischen Terminvorschlägen, Priorisierung der Anfrage.</p>
            </div>
          </div>

          {/* Typische Fehler */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiAlertTriangle} className="mr-3 text-mk-red-600" />
            Typische Fehler bei Trockenbau-Anfragen
          </h2>
          <div className="bg-gray-50 p-6 rounded-xl mb-12">
            <ul className="space-y-3">
              <li className="flex items-center text-gray-700 font-medium"><SafeIcon icon={FiX} className="text-red-500 mr-3"/> „Können Sie mal vorbeikommen?“</li>
              <li className="flex items-center text-gray-700 font-medium"><SafeIcon icon={FiX} className="text-red-500 mr-3"/> „Was kostet eine Wand pro m²?“</li>
              <li className="flex items-center text-gray-700 font-medium"><SafeIcon icon={FiX} className="text-red-500 mr-3"/> „Brauche Trockenbau, melden Sie sich“</li>
            </ul>
            <p className="text-gray-600 text-sm mt-4">
              Diese Anfragen wirken harmlos, führen aber fast immer zu Verzögerungen, ungenauen Angeboten oder keiner Antwort.
            </p>
          </div>

          {/* MUSTER */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Muster für eine perfekte Trockenbau-Anfrage (kopierbar)</h2>
          <p className="text-gray-700 mb-4">Damit kann jedes Trockenbau-Unternehmen sofort kalkulieren.</p>
          <CopyableTemplate />

          {/* Warum sinnvoll für Firmen */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Warum diese Struktur auch für Unternehmen sinnvoll ist</h2>
          <p className="text-gray-700 mb-6">
            Betriebe wie MK-Ausbau nutzen genau diese Informationen, um präzise Angebote zu erstellen, unnötige Termine zu vermeiden und fair und transparent zu kalkulieren. Das spart Zeit auf beiden Seiten – und führt schneller zu guten Ergebnissen.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Gilt das nur für Trockenbau in NRW?</h2>
          <p className="text-gray-700 mb-6">
            Nein. Diese Struktur funktioniert bundesweit für kleine und große Projekte sowie Neubau & Sanierung. In NRW ist sie besonders wichtig, weil es viele Altbauten, viele unterschiedliche Bauweisen und eine hohe Nachfrage im Innenausbau gibt.
          </p>

          <div className="bg-mk-teal-600 text-white p-8 rounded-2xl shadow-xl mt-16 mb-12">
            <h2 className="text-white text-2xl font-bold mb-4 mt-0">Fazit: Gute Anfragen sparen mehr Geld als Nachverhandlungen</h2>
            <p className="text-mk-teal-50 mb-6">
              Eine saubere Trockenbau-Anfrage beschleunigt das Angebot, vermeidet Missverständnisse, schafft Vertrauen und spart Zeit & Kosten.
            </p>
            <p className="text-white font-bold border-t border-mk-teal-400 pt-6">
              👉 Egal welches Trockenbau-Unternehmen: Wer diese Punkte liefert, bekommt bessere Angebote.
            </p>
          </div>

          {/* Embedded Contact Form */}
          <div className="my-20" id="anfrage-formular">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Direkt Anfrage starten
            </h2>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
              <div className="p-4 md:p-6">
                <div className="flex justify-center">
                  <iframe 
                    width="600" 
                    height="850" 
                    src="https://kundenportal.ausbau.nrw/forms/wtl/bd11f7cec082c41c3a99a80708efdffb" 
                    frameBorder="0" 
                    sandbox="allow-top-navigation allow-forms allow-scripts allow-same-origin allow-popups" 
                    allowFullScreen
                    className="w-full max-w-full h-[850px] border-0 rounded-lg"
                    title="Kontaktformular MK-AUSBAU"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-20 pt-12 border-t border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              FAQ: Trockenbau-Anfrage richtig stellen
            </h2>
            <div className="space-y-4">
              {faqSchema.mainEntity.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 hover:bg-mk-teal-50 transition-colors cursor-default">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start">
                    <SafeIcon icon={FiHelpCircle} className="text-mk-teal-500 mt-1 mr-3 flex-shrink-0" />
                    {faq.name}
                  </h3>
                  <p className="text-gray-700 text-sm ml-8 leading-relaxed">
                    {faq.acceptedAnswer.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

        </motion.article>
      </div>
      
      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default TrockenbauAnfrage;